/* eslint-disable prettier/prettier */
interface CSPConfig {
  defaultSrc: string[]
  connectSrc: string[]
  scriptSrc: string[]
  styleSrc: string[]
  imgSrc: string[]
  mediaSrc: string[]
  workerSrc: string[]
  fontSrc: string[]
}

export const getApiDomain = () => {
  const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'https://go-prod.agileview-ai.com'
  try {
    const url = new URL(apiUrl)
    // Special handling for localhost
    if (url.hostname === 'localhost') {
      return url.origin
    }
    // Return the exact hostname instead of trying to create a wildcard pattern
    // This ensures domains like odrg-demo.com work correctly
    return url.hostname
  } catch {
    return ''
  }
}

export const getCSPConfig = (): CSPConfig => {
  const apiDomain = getApiDomain()
  const brand = process.env.REACT_APP_BRAND || 'default'

  // Base configuration that's always needed
  const baseConfig: CSPConfig = {
    defaultSrc: ["'self'"],
    connectSrc: [
      "'self'",
      'blob:',
      'https://*.s3.us-east-1.amazonaws.com',
      'https://*.s3.amazonaws.com',
      'wss://*.agileview-ai.com',
      'ws://*.agileview-ai.com',
      // ODRG uses apex domain only (no subdomains)
      'https://odrg-demo.com',
      'http://odrg-demo.com',
      'ws://odrg-demo.com',
      'wss://odrg-demo.com',
      'http://localhost:*',
      'https://localhost:*',
      'ws://localhost:*',
      'wss://localhost:*',
    ],
    scriptSrc: [
      "'self'",
      "'unsafe-inline'",
      "'unsafe-eval'",
      '*.agileview-ai.com',
      // ODRG apex only
      'odrg-demo.com',
      'localhost:*',
    ],
    styleSrc: ["'self'", "'unsafe-inline'", '*.agileview-ai.com', 'odrg-demo.com', 'localhost:*'],
    imgSrc: [
      "'self'",
      'data:',
      'blob:',
      'server.arcgisonline.com',
      '*.agileview-ai.com',
      // ODRG apex only
      'odrg-demo.com',
      // S3 object URLs
      'https://*.s3.us-east-1.amazonaws.com',
      'https://*.s3.amazonaws.com',
      'localhost:*',
    ],
    mediaSrc: [
      "'self'",
      'blob:',
      '*.agileview-ai.com',
      // ODRG apex only
      'odrg-demo.com',
      // S3 object URLs
      'https://*.s3.us-east-1.amazonaws.com',
      'https://*.s3.amazonaws.com',
      'localhost:*',
    ],
    workerSrc: ["'self'", 'blob:'],
    fontSrc: [
      "'self'",
      'data:',
      '*.agileview-ai.com',
      // ODRG apex only
      'odrg-demo.com',
      'localhost:*',
    ],
  }

  // Add API domain to relevant directives if available
  if (apiDomain) {
    baseConfig.defaultSrc.push(apiDomain)
    baseConfig.connectSrc.push(apiDomain)
    baseConfig.scriptSrc.push(apiDomain)
    baseConfig.styleSrc.push(apiDomain)
    baseConfig.imgSrc.push(apiDomain)
    baseConfig.mediaSrc.push(apiDomain)
    baseConfig.fontSrc.push(apiDomain)
  }

  // Add brand-specific configurations
  switch (brand) {
    case 'odrg':
      // Add any ODRG-specific domains if needed
      break
    // Add cases for other brands as needed
    default:
      break
  }

  return baseConfig
}

export const generateCSPString = (config: CSPConfig): string => {
  const directives = [
    `default-src ${config.defaultSrc.join(' ')}`,
    `connect-src ${config.connectSrc.join(' ')}`,
    `script-src ${config.scriptSrc.join(' ')}`,
    `style-src ${config.styleSrc.join(' ')}`,
    `img-src ${config.imgSrc.join(' ')}`,
    `media-src ${config.mediaSrc.join(' ')}`,
    `worker-src ${config.workerSrc.join(' ')}`,
    `font-src ${config.fontSrc.join(' ')}`,
  ]

  return directives.join('; ')
}

export const injectCSP = () => {
  const config = getCSPConfig()
  const cspString = generateCSPString(config)

  let meta = document.querySelector('meta[http-equiv="Content-Security-Policy"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('http-equiv', 'Content-Security-Policy')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', cspString)
}
